import { useId } from "react";

import { useT } from "@/i18n";
import {
  formatMaintenanceTime,
  localizeMaintenanceText,
  MaintenanceSchedule,
} from "@/lib/maintenance/schedule";
import { IconArrowLink, TextButtonLink } from "@/ui";

type MaintenanceBlockScreenProps = {
  schedule: MaintenanceSchedule | null;
  locale: string;
};

export const MaintenanceBlockScreen = ({ schedule, locale }: MaintenanceBlockScreenProps) => {
  const t = useT();
  const titleId = useId();
  const messageId = useId();

  const fallbackMessage = schedule?.endsAt
    ? t("maintenance.components.notice.blockedMessageUntil", {
        end: formatMaintenanceTime(schedule.endsAt, locale),
      })
    : t("maintenance.components.notice.blockedMessage");

  const title =
    (schedule && localizeMaintenanceText(schedule, "inProgress", "title", locale)) ??
    t("maintenance.components.notice.blockedTitle");
  const message =
    (schedule && localizeMaintenanceText(schedule, "inProgress", "message", locale)) ??
    fallbackMessage;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      className="fixed inset-0 z-[2147483001] flex items-center justify-center bg-surface-1 p-6 text-center"
    >
      <div className="flex max-w-[480px] flex-col items-center gap-2">
        <h1 id={titleId} className="preset-headline-sm text-new-primary">
          {title}
        </h1>
        <p id={messageId} className="preset-body-md text-secondary">
          {message}
        </p>
        {schedule?.link && (
          <TextButtonLink
            href={schedule.link}
            target="_blank"
            rel="noopener noreferrer"
            color="brand"
            endIcon={IconArrowLink}
            className="mt-4"
          >
            {t("maintenance.components.notice.moreInfo")}
          </TextButtonLink>
        )}
      </div>
    </div>
  );
};
